import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import PageContainer, { BreadCrumbLink } from '../../components/PageContainer/PageContainer';
import { saveDish, uploadDishImage, deleteDishImage } from './slice';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { AddDishContainer, AddDishTitle, AddDishContentHeader, AddDishContentBody, FieldsGrid } from './Menu.styled';
import { DishRequest, Dish } from './types';
import { selectCurrentRestaurant, setOpen, selectShouldOpen, selectMessage, selectSeverity } from '../Restaurant/slice';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';

const EditDish: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { alias, menuID, dishID } = useParams();
  const restaurant = useAppSelector(selectCurrentRestaurant);
  const menu = restaurant?.menus?.find(m => m.menuID === menuID);
  const initialDish = (location.state?.dish as Dish) ?? menu?.dishes?.find(d => d.dishID === dishID);

  const [name, setName] = useState(initialDish?.name ?? '');
  const [description, setDescription] = useState(initialDish?.description ?? '');
  const [price, setPrice] = useState(initialDish?.price?.toString() ?? '');
  const [dietary, setDietary] = useState(initialDish?.dietary ?? '');
  const [unsuitableDietary, setUnsuitableDietary] = useState(initialDish?.unsuitabledietary ?? '');
  const [ingredients, setIngredients] = useState(initialDish?.ingredients ?? '');
  const [preparation, setPreparation] = useState(initialDish?.preparation ?? '');
  const [tags, setTags] = useState(initialDish?.tags ?? '');
  // eslint-disable-next-line
  const [imageUrl, setImageurl] = useState(initialDish?.imageUrl ?? '');
  const [imagePreview, setImagePreview] = useState<string | null>(initialDish?.imageUrl ?? null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update form state when dish data changes
  useEffect(() => {
    if (initialDish != null) {
      setName(initialDish.name ?? '');
      setDescription(initialDish.description ?? '');
      setPrice(initialDish.price?.toString() ?? '');
      setDietary(initialDish.dietary ?? '');
      setUnsuitableDietary(initialDish.unsuitabledietary ?? '');
      setIngredients(initialDish.ingredients ?? '');
      setPreparation(initialDish.preparation ?? '');
      setTags(initialDish.tags ?? '');
      setImageurl(initialDish.imageUrl ?? '');
      setImagePreview(initialDish.imageUrl ?? null);
    }
  }, [initialDish]);

  // Update image preview when imageUrl changes (from Redux state updates)
  useEffect(() => {
    const currentDish = menu?.dishes?.find(d => d.dishID === dishID);
    if (currentDish?.imageUrl != null) {
      setImageurl(currentDish.imageUrl);
      setImagePreview(currentDish.imageUrl);
    } else if (currentDish?.imageUrl === undefined || currentDish?.imageUrl === null) {
      setImageurl('');
      setImagePreview(null);
    }
  }, [menu, dishID]);

  const breadcrumbLinks: BreadCrumbLink[] = [
    {
      name: restaurant?.name ?? '',
      url: `/restaurant/${alias}`
    },
    {
      name: menu?.name ?? '',
      url: `/restaurant/${alias}/menu/${menuID}`
    }
  ];

  const openServer = useAppSelector(selectShouldOpen);
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (file != null && dishID != null) {
      if (file.type.startsWith('image/')) {
        setIsUploadingImage(true);
        try {
          const result = await dispatch(uploadDishImage({ dishId: dishID, file })).unwrap();
          setImagePreview(result.imageUrl);
          setImageurl(result.imageUrl);
        } catch (error) {
          console.error('Failed to upload dish image:', error);
        } finally {
          setIsUploadingImage(false);
          if (fileInputRef.current != null) {
            fileInputRef.current.value = '';
          }
        }
      }
    }
  };

  const handleDeleteImage = async (): Promise<void> => {
    if (dishID != null && window.confirm('Are you sure you want to remove this image?')) {
      setIsDeletingImage(true);
      try {
        await dispatch(deleteDishImage({ dishId: dishID })).unwrap();
        setImagePreview(null);
        setImageurl('');
      } catch (error) {
        console.error('Failed to delete dish image:', error);
      } finally {
        setIsDeletingImage(false);
      }
    }
  };

  const handleSaveDish = async (): Promise<void> => {
    // Prevent multiple submissions - early return if already saving
    if (isSaving || isUploadingImage || isDeletingImage) {
      return;
    }
    
    setIsSaving(true);
    const dishRequest: DishRequest = {
      menuId: menuID ?? '',
      dishId: dishID ?? '',
      name,
      price: Number(price),
      description,
      dietary,
      unsuitabledietary: unsuitableDietary,
      ingredients,
      preparation,
      tags,
      imageUrl: imageUrl && imageUrl.trim() !== '' ? imageUrl : undefined
    };
    try {
      await dispatch(saveDish(dishRequest)).unwrap();
      navigate(`/restaurant/${alias}/menu/${menuID}`);
    } catch (error) {
      console.error('Failed to save dish:', error);
      // Error is already handled by Redux slice, but we ensure isSaving is reset
      // Don't navigate on error - let user see the error message
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer
      title={`Edit ${name !== '' ? name : 'Dish'}`}
      breadcrumbs={breadcrumbLinks}
      open={openServer}
      setOpen={setOpenServer}
      message={message}
      severity={severity}
    >
      <AddDishContainer>
        <AddDishContentHeader>
          <AddDishTitle>Edit Dish</AddDishTitle>
        </AddDishContentHeader>
        <AddDishContentBody>
          <FieldsGrid>
            <TextField
              fullWidth
              label="Dish Name"
              variant="outlined"
              value={name}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setName(event.target.value);
              }}
              size="small"
            />
            <TextField
              fullWidth
              label="Price"
              variant="outlined"
              type="number"
              inputProps={{ step: '0.01' }}
              value={price}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPrice(event.target.value);
              }}
              size="small"
            />
          </FieldsGrid>

          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            multiline
            rows={3}
            value={description}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setDescription(event.target.value);
            }}
          />

          <TextField
            fullWidth
            label="Ingredients"
            variant="outlined"
            multiline
            rows={3}
            value={ingredients}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setIngredients(event.target.value);
            }}
          />

          <TextField
            fullWidth
            label="Preparation"
            variant="outlined"
            value={preparation}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setPreparation(event.target.value);
            }}
            size="small"
          />

          <FieldsGrid>
            <TextField
              fullWidth
              label="Dietary"
              variant="outlined"
              value={dietary}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setDietary(event.target.value);
              }}
              size="small"
            />
            <TextField
              fullWidth
              label="Unsuitable Dietary"
              variant="outlined"
              value={unsuitableDietary}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setUnsuitableDietary(event.target.value);
              }}
              size="small"
            />
          </FieldsGrid>

          <TextField
            fullWidth
            label="Tags"
            variant="outlined"
            multiline
            rows={2}
            value={tags}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setTags(event.target.value);
            }}
          />

          {/* Dish Image Upload Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                void handleFileSelect(e);
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                fileInputRef.current?.click();
              }}
              disabled={isSaving || isUploadingImage || isDeletingImage}
            >
              {imagePreview != null ? 'Change Image' : 'Select Dish Image'}
            </Button>
            {imagePreview != null && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                <Box sx={{ position: 'relative', display: 'inline-block', alignSelf: 'flex-start' }}>
                  <Avatar
                    src={imagePreview}
                    alt="Dish preview"
                    sx={{ width: 150, height: 150 }}
                    variant="rounded"
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      void handleDeleteImage();
                    }}
                    disabled={isDeletingImage || isUploadingImage}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                {isDeletingImage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CircularProgress size={16} />
                    <span style={{ fontSize: '14px', color: '#666' }}>Removing image...</span>
                  </Box>
                )}
              </Box>
            )}
            {isUploadingImage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CircularProgress size={16} />
                <span style={{ fontSize: '14px', color: '#666' }}>Uploading image...</span>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                void handleSaveDish();
              }}
              disabled={isSaving || isUploadingImage || isDeletingImage}
              startIcon={isSaving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                navigate(`/restaurant/${alias}/menu/${menuID}`);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Box>
        </AddDishContentBody>
      </AddDishContainer>
    </PageContainer>
  );
};

export default withAuthenticationRequired(EditDish);
